/**
 * Generates SVG authenticity badges for C2PA-verified files.
 * All generation happens client-side — no server required.
 *
 * Badge states map to C2PA verification outcomes:
 *   trusted  — Valid signature + trusted signer (green, CR icon)
 *   valid    — Valid signature, untrusted signer (amber, CR icon with warning)
 *   invalid  — Signature errors / tampered (red, CR icon with X)
 *   unsigned — No C2PA metadata (gray, no CR icon)
 *
 * The CR (Content Credentials) pin icon is the official C2PA icon from
 * github.com/contentauth/verify-site, used under open-source terms.
 */

export type BadgeStatus = 'trusted' | 'valid' | 'invalid' | 'unsigned'
export type BadgeStyle = 'flat' | 'classic' | 'minimal'

interface BadgeColors {
  bg: string
  text: string
  accent: string
  border: string
  dot: string
  /** Header bar color — darker shade for visual hierarchy */
  header: string
}

const STATUS_COLORS: Record<BadgeStatus, BadgeColors> = {
  trusted: {
    bg: '#f0fdf4',
    text: '#065f46',
    accent: '#059669',
    border: '#6ee7b7',
    dot: '#10b981',
    header: '#064e3b',
  },
  valid: {
    bg: '#fffbeb',
    text: '#78350f',
    accent: '#d97706',
    border: '#fcd34d',
    dot: '#f59e0b',
    header: '#78350f',
  },
  invalid: {
    bg: '#fef2f2',
    text: '#7f1d1d',
    accent: '#dc2626',
    border: '#fca5a5',
    dot: '#ef4444',
    header: '#7f1d1d',
  },
  unsigned: {
    bg: '#f9fafb',
    text: '#374151',
    accent: '#6b7280',
    border: '#d1d5db',
    dot: '#9ca3af',
    header: '#1f2937',
  },
}

const STATUS_LABELS: Record<BadgeStatus, string> = {
  trusted: 'Verified',
  valid: 'Valid',
  invalid: 'Invalid',
  unsigned: 'No provenance',
}

const STATUS_SUBLABELS: Record<BadgeStatus, string> = {
  trusted: 'File Details',
  valid: 'File Details',
  invalid: 'File Details',
  unsigned: 'File Verified',
}

/** Shield icon path for the Verifieddit logo (24×24 viewBox) */
const SHIELD_PATH =
  'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z'

// ── Official CR (Content Credentials) icon paths ─────────────────────────────
// Source: github.com/contentauth/verify-site — assets/svg/color/
// The CR pin is the rounded-corner rectangle with "Cr" letterforms inside.
// viewBox: 0 0 16 16 (normalized from original 18×18, offset by 1,1)

/** Base CR pin outline + "Cr" letters (16×16 viewBox, fill=currentColor) */
const CR_BASE_PATH = 'M14.6,8v6.6H8c-3.7,0-6.6-3-6.6-6.6s3-6.6,6.6-6.6S14.6,4.3,14.6,8z M0,8c0-4.4,3.6-8,8-8s8,3.6,8,8v8H8C3.6,16,0,12.4,0,8z M3.2,8.3c0,1.6,1.1,3,2.9,3c1.5,0,2.4-1,2.7-2.2H7.3c-0.2,0.6-0.6,0.9-1.2,0.9c-0.9,0-1.5-0.7-1.5-1.8s0.6-1.8,1.5-1.8c0.6,0,1,0.3,1.2,0.9h1.4C8.5,6.2,7.5,5.3,6.1,5.3C4.3,5.3,3.2,6.7,3.2,8.3z M10.7,5.4H9.3v5.8h1.4v-3c0-0.6,0.2-0.9,0.4-1.2c0.2-0.2,0.6-0.3,1.1-0.3h0.4V5.4h-0.4c-0.8,0-1.2,0.3-1.6,0.7L10.7,5.4L10.7,5.4z'

/** Red X badge overlay for invalid state (bottom-right corner) */
const CR_INVALID_BADGE = `<rect x="11" y="11" width="7" height="7" rx="1" fill="#d31510"/>
<path fill="white" d="M16.1,12.4L14.5,14l-1.6-1.6c-0.1-0.1-0.2-0.1-0.3,0l-0.3,0.3c-0.1,0.1-0.1,0.2,0,0.3l1.6,1.6l-1.6,1.6c-0.1,0.1-0.1,0.2,0,0.3l0.3,0.3c0.1,0.1,0.2,0.1,0.3,0l1.6-1.6l1.6,1.6c0.1,0.1,0.2,0.1,0.3,0l0.3-0.3c0.1-0.1,0.1-0.2,0-0.3L15,14.5l1.6-1.6c0.1-0.1,0.1-0.2,0-0.3l-0.3-0.3c0,0-0.1-0.1-0.1-0.1S16.1,12.3,16.1,12.4z"/>`

/** Amber warning triangle overlay for untrusted/valid state (bottom-right corner) */
const CR_WARNING_BADGE = `<path fill-rule="evenodd" clip-rule="evenodd" d="M12,18c-0.7,0-1.2-0.8-0.9-1.4l2.5-5c0.4-0.7,1.4-0.7,1.8,0l2.5,5c0.3,0.7-0.2,1.4-0.9,1.4H12z" fill="#E46F00"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M14.8,15.7c0.1,0,0.1-0.1,0.1-0.1v-2.7c0-0.1-0.1-0.1-0.1-0.1h-0.7c-0.1,0-0.1,0.1-0.1,0.1v2.7c0,0.1,0.1,0.1,0.1,0.1H14.8z M14.8,17.1c0.1,0,0.1-0.1,0.1-0.1v-0.7c0-0.1-0.1-0.1-0.1-0.1h-0.7c-0.1,0-0.1,0.1-0.1,0.1v0.7c0,0.1,0.1,0.1,0.1,0.1H14.8z" fill="white"/>`

/**
 * Render the CR icon SVG group at a given position and size.
 * For 'trusted': clean CR pin in status color
 * For 'valid': CR pin with amber warning triangle
 * For 'invalid': CR pin with red X badge
 * For 'unsigned': no CR icon rendered
 */
function renderCrIcon(status: BadgeStatus, x: number, y: number, size: number): string {
  if (status === 'unsigned') return ''
  const colors = STATUS_COLORS[status]
  const scale = size / 18 // original viewBox is 18×18 (paths use 0–16 range + margin)
  let overlay = ''
  if (status === 'invalid') overlay = CR_INVALID_BADGE
  else if (status === 'valid') overlay = CR_WARNING_BADGE

  return `<g transform="translate(${x}, ${y}) scale(${scale.toFixed(4)})">
    <g transform="translate(1, 1)">
      <path fill-rule="evenodd" clip-rule="evenodd" fill="${colors.accent}" d="${CR_BASE_PATH}"/>
      ${overlay}
    </g>
  </g>`
}

/**
 * Generate a flat horizontal badge SVG (160×28).
 */
export function generateBadgeSvg(
  status: BadgeStatus,
  style: BadgeStyle = 'flat',
  filename?: string,
  qrCodeDataUri?: string
): string {
  const colors = STATUS_COLORS[status]
  const label = STATUS_LABELS[status]

  if (style === 'minimal') {
    return generateMinimalBadge(status, colors, label)
  }

  if (style === 'classic') {
    return generateClassicBadge(status, colors, label, filename, qrCodeDataUri)
  }

  return generateFlatBadge(status, colors, label)
}

function generateFlatBadge(status: BadgeStatus, colors: BadgeColors, label: string): string {
  const hasCr = status !== 'unsigned'
  const w = hasCr ? 175 : 160
  const h = 28
  const logoW = 32
  const crSize = 16
  const crX = logoW + 6
  const crY = (h - crSize) / 2
  const textX = hasCr ? crX + crSize + 5 : logoW + 20

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" role="img" aria-label="${label} by verifieddit.com">
  <title>${label} by verifieddit.com</title>
  <defs>
    <linearGradient id="bg-${status}" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="${status === 'unsigned' ? '#f9fafb' : colors.bg}"/>
      <stop offset="100%" stop-color="${status === 'unsigned' ? '#f3f4f6' : colors.bg}"/>
    </linearGradient>
    <clipPath id="r-${status}">
      <rect width="${w}" height="${h}" rx="6" ry="6"/>
    </clipPath>
  </defs>
  <g clip-path="url(#r-${status})">
    <rect width="${logoW}" height="${h}" fill="${colors.header}"/>
    <rect x="${logoW}" width="${w - logoW}" height="${h}" fill="url(#bg-${status})"/>
    <rect width="${w}" height="${h}" rx="6" ry="6" fill="none" stroke="${colors.border}" stroke-width="1.5"/>
    <g transform="translate(5, 4) scale(0.833)">
      <path d="${SHIELD_PATH}" fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </g>
    ${hasCr ? renderCrIcon(status, crX, crY, crSize) : `<circle cx="${logoW + 10}" cy="${h / 2}" r="4" fill="${colors.dot}"/>`}
    <text x="${textX}" y="${h / 2 + 4.5}" font-family="system-ui, -apple-system, sans-serif" font-size="11" font-weight="600" fill="${colors.text}" letter-spacing="0.3">${label}</text>
  </g>
</svg>`
}

function generateClassicBadge(
  status: BadgeStatus,
  colors: BadgeColors,
  label: string,
  filename?: string,
  qrCodeDataUri?: string
): string {
  const hasQr = !!qrCodeDataUri
  const hasCr = status !== 'unsigned'
  const w = hasQr ? 300 : 220
  const h = hasQr ? 88 : 58
  const textW = hasQr ? 210 : w
  const qrSize = 68
  const qrX = textW + Math.round((w - textW - qrSize) / 2)
  const qrY = Math.round((h - qrSize) / 2)
  const subtext = filename ? shortenFilename(filename, hasQr ? 22 : 24) : 'verifieddit.com'

  // CR icon placement in the status row
  const crSize = 22
  const statusY = hasQr ? 32 : 26
  const crX = 10
  const crY = statusY + (hasQr ? -1 : -1)
  const labelX = hasCr ? crX + crSize + 5 : 24
  const labelY = statusY + (hasQr ? 14 : 12)

  // Sublabel (Content Credentials / File Verified)
  const sublabel = STATUS_SUBLABELS[status]

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" role="img" aria-label="${label} — ${sublabel} by verifieddit.com">
  <title>${label} — ${sublabel} by verifieddit.com</title>
  <defs>
    <clipPath id="cr-${status}${hasQr ? '-qr' : ''}">
      <rect width="${w}" height="${h}" rx="10" ry="10"/>
    </clipPath>
  </defs>
  <g clip-path="url(#cr-${status}${hasQr ? '-qr' : ''})">
    <!-- header bar -->
    <rect width="${textW}" height="22" fill="${colors.header}"/>
    <!-- body -->
    <rect y="22" width="${textW}" height="${h - 22}" fill="${colors.bg}"/>
    <!-- outer border -->
    <rect width="${w}" height="${h}" rx="10" ry="10" fill="none" stroke="${colors.border}" stroke-width="1.5"/>
    <!-- Brand: verifieddit.com in header -->
    <g transform="translate(8, 3.5) scale(0.625)">
      <path d="${SHIELD_PATH}" fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </g>
    <text x="26" y="15.5" font-family="system-ui, -apple-system, sans-serif" font-size="10" font-weight="700" fill="#ffffff" letter-spacing="0.5">VERIFIEDDIT.COM</text>
    <!-- status indicator row -->
    ${hasCr ? renderCrIcon(status, crX, crY, crSize) : `<circle cx="20" cy="${labelY - 4}" r="5" fill="${colors.dot}"/>`}
    <text x="${labelX}" y="${labelY}" font-family="system-ui, -apple-system, sans-serif" font-size="13" font-weight="700" fill="${colors.text}">${label}</text>
    ${hasCr ? `<text x="${labelX}" y="${labelY + 14}" font-family="system-ui, -apple-system, sans-serif" font-size="9" font-weight="500" fill="${colors.accent}" letter-spacing="0.3">${sublabel}</text>` : ''}
    <!-- filename subtext -->
    <text x="12" y="${h - 8}" font-family="system-ui, -apple-system, sans-serif" font-size="10" font-weight="500" fill="${colors.accent}" opacity="0.8" letter-spacing="0.2">${subtext}</text>
    ${hasQr ? `<!-- QR code panel -->
    <rect x="${textW}" y="0" width="${w - textW}" height="${h}" fill="#ffffff"/>
    <rect x="${textW}" y="0" width="1" height="${h}" fill="${colors.border}"/>
    <image href="${qrCodeDataUri}" x="${qrX}" y="${qrY}" width="${qrSize}" height="${qrSize}"/>` : ''}
  </g>
</svg>`
}

function generateMinimalBadge(status: BadgeStatus, colors: BadgeColors, label: string): string {
  const hasCr = status !== 'unsigned'
  const w = hasCr ? 130 : 120
  const h = 24
  const crSize = 14
  const crX = 6
  const crY = (h - crSize) / 2
  const textX = hasCr ? crX + crSize + 5 : 24

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" role="img" aria-label="${label}">
  <title>${label}</title>
  <defs>
    <clipPath id="mr-${status}">
      <rect width="${w}" height="${h}" rx="12" ry="12"/>
    </clipPath>
  </defs>
  <g clip-path="url(#mr-${status})">
    <rect width="${w}" height="${h}" fill="${colors.bg}"/>
    <rect width="${w}" height="${h}" rx="12" ry="12" fill="none" stroke="${colors.border}" stroke-width="1.5"/>
    ${hasCr ? renderCrIcon(status, crX, crY, crSize) : `<circle cx="14" cy="12" r="4.5" fill="${colors.dot}"/>`}
    <text x="${textX}" y="16" font-family="system-ui, -apple-system, sans-serif" font-size="10.5" font-weight="600" fill="${colors.text}" letter-spacing="0.2">${label}</text>
  </g>
</svg>`
}

function shortenFilename(name: string, maxLen: number): string {
  if (name.length <= maxLen) return name
  const ext = name.lastIndexOf('.')
  if (ext > 0) {
    const base = name.slice(0, ext)
    const extension = name.slice(ext)
    const allowedBase = maxLen - extension.length - 2
    return base.slice(0, allowedBase) + '…' + extension
  }
  return name.slice(0, maxLen - 1) + '…'
}

/** Convert SVG string to a data URI for use in <img> tags */
export function svgToDataUri(svg: string): string {
  return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg)
}

/** Convert SVG string to a downloadable blob URL */
export function svgToBlobUrl(svg: string): string {
  const blob = new Blob([svg], { type: 'image/svg+xml' })
  return URL.createObjectURL(blob)
}

/** Generate all badge variants for a given status */
export function generateAllBadges(
  status: BadgeStatus,
  filename?: string
): { flat: string; classic: string; minimal: string } {
  return {
    flat: generateBadgeSvg(status, 'flat'),
    classic: generateBadgeSvg(status, 'classic', filename),
    minimal: generateBadgeSvg(status, 'minimal'),
  }
}

/** Generate HTML embed snippet */
export function generateHtmlEmbed(status: BadgeStatus, style: BadgeStyle = 'classic', filename?: string, qrCodeDataUri?: string, shortId?: string, uuid?: string): string {
  const svg = generateBadgeSvg(status, style, filename, qrCodeDataUri)
  const dataUri = svgToDataUri(svg)
  const label = STATUS_LABELS[status]
  const linkUrl = buildBadgeTrackingUrl(undefined, shortId, uuid)
  const hasQr = !!qrCodeDataUri
  const dims =
    style === 'flat'
      ? status !== 'unsigned' ? 'width="175" height="28"' : 'width="160" height="28"'
      : style === 'classic'
        ? hasQr ? 'width="300" height="88"' : 'width="220" height="58"'
        : status !== 'unsigned' ? 'width="130" height="24"' : 'width="120" height="24"'
  return `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer" title="Verified by verifieddit.com">
  <img src="${dataUri}" alt="${label} by verifieddit.com" ${dims}/>
</a>`
}

/** Generate Markdown embed snippet */
export function generateMarkdownEmbed(status: BadgeStatus, style: BadgeStyle = 'classic', filename?: string, qrCodeDataUri?: string, shortId?: string, uuid?: string): string {
  const svg = generateBadgeSvg(status, style, filename, qrCodeDataUri)
  const dataUri = svgToDataUri(svg)
  const label = STATUS_LABELS[status]
  const linkUrl = buildBadgeTrackingUrl(undefined, shortId, uuid)
  return `[![${label} by verifieddit.com](${dataUri})](${linkUrl})`
}

/**
 * Derive a unique badge download filename.
 * Fallback chain: shortId → uuid prefix → fileHash prefix → generic name.
 * Ensures badges always have a unique filename when any identifier exists.
 */
export function getBadgeFilename(opts: {
  shortId?: string | null
  uuid?: string | null
  fileHash?: string | null
  status: BadgeStatus
}): string {
  if (opts.shortId) return `${opts.shortId}.svg`
  if (opts.uuid) return `${opts.uuid.slice(0, 8)}.svg`
  if (opts.fileHash && opts.fileHash.length >= 16) return `${opts.fileHash.slice(0, 16)}.svg`
  return `verifieddit-badge-${opts.status}.svg`
}

/**
 * Build the unique tracking URL for a badge QR code.
 * When a shortId is provided, use the verifieddit.com verify URL.
 * When only a uuid is available (guest/anonymous), link to the proof page directly.
 * Otherwise, fall back to legacy verifieddit.com tracking URL.
 */
export function buildBadgeTrackingUrl(sha256?: string, shortId?: string, uuid?: string): string {
  // Prefer verifieddit.com verify URL when available
  if (shortId) {
    return `https://verifieddit.com/verify/${shortId}`
  }
  // Proof page fallback (guest users with a verification record)
  if (uuid) {
    return `https://verifieddit.com/proof/${uuid}`
  }
  // Legacy fallback
  const base = 'https://www.verifieddit.com/'
  if (sha256 && sha256.length >= 8) {
    const id = sha256.slice(0, 16)
    return `${base}?ref=badge&id=${id}`
  }
  return `${base}?ref=badge`
}
